import { useState } from 'preact/hooks';
import { isoDateIn, parseTasks, TaskListConfigSchema, type TaskListConfig } from '@hud/core';
import { registerWidget, type WidgetProps } from '../registry.js';
import { useHud } from '../context.js';
import { useStore } from '../hooks/useStore.js';
import { useNow } from '../hooks/useNow.js';
import './tasklist.css';

/**
 * Checkboxes from a markdown file, tickable in place.
 *
 * A toggle enqueues an intent and repaints immediately; the write lands a
 * second later and produces a one-line diff in the file. Nothing here waits on
 * the network — see `store.ts`.
 */
function TaskList({ config }: WidgetProps<TaskListConfig>) {
  // Re-resolve `{today}` on a timer so a HUD left open overnight rolls over to
  // tomorrow's file on its own instead of showing yesterday until reloaded.
  const now = useNow(60_000);
  const { config: vault } = useHud();
  const path = config.file.replace('{today}', isoDateIn(now, config.timezone ?? vault.timezone));

  const store = useStore();
  const [adding, setAdding] = useState('');

  const content = store.get(path);
  let tasks = parseTasks(content ?? '');

  if (config.hideCompleted) tasks = tasks.filter((task) => !task.checked);
  if (config.completedLast) {
    tasks = [...tasks].sort((a, b) => Number(a.checked) - Number(b.checked));
  }
  const visible = config.limit === undefined ? tasks : tasks.slice(0, config.limit);

  const submitNew = (event: Event) => {
    event.preventDefault();
    const text = adding.trim();
    if (!text) return;
    // Creating the file implicitly: `addTask` on missing content appends to an
    // empty string, and the write creates it. A new day needs no setup.
    store.mutate({ kind: 'addTask', path, text });
    setAdding('');
  };

  return (
    <div class="hud-tasks-wrap">
      {visible.length === 0 ? (
        <div class="hud-tasks__empty">{config.empty}</div>
      ) : (
        <ul class="hud-tasks">
          {visible.map((task) => (
            <li
              class={task.checked ? 'hud-tasks__item hud-tasks__item--done' : 'hud-tasks__item'}
              key={`${task.line}:${task.text}`}
            >
              <label class="hud-tasks__label">
                <input
                  class="hud-tasks__input"
                  type="checkbox"
                  checked={task.checked}
                  onChange={() =>
                    store.mutate({
                      kind: 'toggleTask',
                      path,
                      line: task.line,
                      text: task.text,
                      checked: !task.checked,
                    })
                  }
                />
                <span class="hud-tasks__box" aria-hidden="true" />
                <span class="hud-tasks__text">{task.text}</span>
              </label>
              <button
                class="hud-tasks__remove"
                type="button"
                aria-label={`Remove ${task.text}`}
                onClick={() =>
                  store.mutate({ kind: 'removeTask', path, line: task.line, text: task.text })
                }
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      <form class="hud-tasks__add" onSubmit={submitNew}>
        <input
          class="hud-tasks__new"
          type="text"
          value={adding}
          placeholder="Add a task…"
          onInput={(event) => setAdding((event.target as HTMLInputElement).value)}
        />
      </form>
    </div>
  );
}

registerWidget({
  type: 'tasklist',
  schema: TaskListConfigSchema,
  component: TaskList,
});
